/**
 * 이미지 한 장 → PBR 맵 한 벌.
 *
 * 핵심 발상은 주파수 분리다.
 *   - **저주파** 밝기 = 원본을 찍을 때의 조명. albedo에서 빼야 할 것. (디라이팅)
 *   - **중주파** 요철 = 표면 형상. 노멀맵으로 간다.
 *   - **고주파** 잡음 = 표면 거칠기. 러프니스로 간다.
 *   - **아주 밝고 채도 없는 점** = 스페큘러. 여기는 매끄럽고, 금속일 확률이 높다.
 *
 * 물리적으로 옳은 역산이 아니다. 역산은 원래 불가능하다(조명과 재질이 곱해진 뒤라).
 * 목표는 "같은 이미지를 다시 조명했을 때 그럴듯하게 보이는 재질"이다.
 */

import type { Field, ForgeParams, ImageAnalysis, MaterialMaps, Raster } from '../types.ts';
import {
  boxBlur,
  clamp,
  cloneField,
  encodeSrgb,
  gaussianBlur,
  linearize,
  localVariance,
  luminanceField,
  makeField,
  makeRaster,
  mapField,
  normalizeField,
  saturationAt,
  smoothstep,
  sobel,
} from '../imaging/raster.ts';

/** 마스크 안쪽 평균. 배경이 통계를 끌고 가는 걸 막는다. */
function maskedMean(f: Field, mask: Field): number {
  let sum = 0;
  let count = 0;
  for (let i = 0; i < f.data.length; i++) {
    const m = mask.data[i]!;
    if (m < 0.5) continue;
    sum += f.data[i]!;
    count++;
  }
  return count ? sum / count : 0;
}

/**
 * 디라이팅. 저주파 조명 추정치로 나눠서 평평하게 만든다.
 *
 * 나누기만 하면 어두운 구석이 폭발하므로 이득에 상한을 씌우고,
 * 스페큘러로 날아간 하이라이트는 소프트 니로 눌러 색을 되살린다.
 */
export function delight(src: Raster, luma: Field, mask: Field, amount: number): Raster {
  const out = makeRaster(src.width, src.height);
  if (amount <= 0.001) {
    out.data.set(src.data);
    return out;
  }

  const sigma = Math.max(6, Math.max(src.width, src.height) * 0.14);
  const illumination = gaussianBlur(luma, sigma);
  const target = Math.max(0.02, maskedMean(luma, mask));

  for (let i = 0, p = 0; i < luma.data.length; i++, p += 4) {
    const ill = Math.max(0.015, illumination.data[i]!);
    // 이득 상한 2.6배, 하한 0.45배. 넘어가면 그림자가 얼룩으로 남는다.
    const rawGain = target / ill;
    const gain = 1 + (clamp(rawGain, 0.45, 2.6) - 1) * amount;

    const l = luma.data[i]!;
    const sat = saturationAt(src.data, p);
    // 밝고 채도 낮은 점 = 스페큘러. albedo에서 걷어낸다.
    const specular = smoothstep(0.72, 0.99, l) * (1 - smoothstep(0.1, 0.45, sat));
    const specKill = 1 - specular * 0.55 * amount;

    for (let c = 0; c < 3; c++) {
      const lin = linearize(src.data[p + c]!);
      out.data[p + c] = encodeSrgb(lin * gain * specKill);
    }
    out.data[p + 3] = src.data[p + 3]!;
  }
  return out;
}

/**
 * 가장자리 번짐(edge bleed).
 *
 * 배경을 걷어낸 그림의 실루엣 한두 픽셀은 배경색과 섞인 안티에일리어싱 값이다.
 * 이걸 그대로 텍스처로 쓰면 메시 테두리와 옆면 띠에 흰 테가 두른다 — 갤러리의
 * 슬라임과 기사에서 실제로 보였다. 테두리 띠 안쪽 픽셀을 그 바로 안쪽 색으로
 * 덮어써서 지운다. 밉맵이 내려갈 때 배경색이 스며드는 것도 같이 막는다.
 */
export function bleedEdges(src: Raster, mask: Field, bandRadius = 2, passes = 4): Raster {
  const { width: w, height: h } = src;
  const out: Raster = { width: w, height: h, data: new Uint8ClampedArray(src.data) };

  // 띠 = 안쪽이면서 (bandRadius 안에) 바깥이 있는 픽셀.
  const around = boxBlur(mask, bandRadius);
  const confident = new Uint8Array(w * h);
  let bandCount = 0;
  for (let i = 0; i < confident.length; i++) {
    const inside = mask.data[i]! >= 0.5;
    const solid = inside && around.data[i]! > 0.985;
    confident[i] = solid ? 1 : 0;
    if (inside && !solid) bandCount++;
  }
  if (bandCount === 0) return out;

  const next = new Uint8Array(w * h);
  for (let pass = 0; pass < passes; pass++) {
    next.set(confident);
    let changed = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (confident[i]) continue;
        let r = 0, g = 0, b = 0, n = 0;
        for (let dy = -1; dy <= 1; dy++) {
          const yy = y + dy;
          if (yy < 0 || yy >= h) continue;
          for (let dx = -1; dx <= 1; dx++) {
            const xx = x + dx;
            if (xx < 0 || xx >= w) continue;
            const j = yy * w + xx;
            if (!confident[j]) continue;
            const p = j * 4;
            r += out.data[p]!; g += out.data[p + 1]!; b += out.data[p + 2]!; n++;
          }
        }
        if (n === 0) continue;
        const p = i * 4;
        out.data[p] = r / n; out.data[p + 1] = g / n; out.data[p + 2] = b / n;
        next[i] = 1;
        changed++;
      }
    }
    confident.set(next);
    if (changed === 0) break;
  }
  return out;
}

/** 저주파를 뺀 형상 성분. 노멀·러프니스·AO가 전부 이걸 먹는다. */
function surfaceDetail(luma: Field, maxDim: number): Field {
  const base = gaussianBlur(luma, Math.max(3, maxDim * 0.05));
  const soft = gaussianBlur(luma, 0.9);
  return { width: luma.width, height: luma.height, data: soft.data.map((v, i) => v - base.data[i]!) as Float32Array };
}

/** 접선공간 노멀맵. OpenGL 규약(초록 = 위). */
export function buildNormal(detail: Field, mask: Field, strength: number): Raster {
  const { gx, gy } = sobel(detail);
  const out = makeRaster(detail.width, detail.height);
  const k = strength * 12;

  for (let i = 0, p = 0; i < detail.data.length; i++, p += 4) {
    const m = mask.data[i]!;
    const nx = -gx.data[i]! * k * m;
    const ny = gy.data[i]! * k * m;
    const inv = 1 / Math.sqrt(nx * nx + ny * ny + 1);
    out.data[p] = (nx * inv * 0.5 + 0.5) * 255;
    out.data[p + 1] = (ny * inv * 0.5 + 0.5) * 255;
    out.data[p + 2] = (inv * 0.5 + 0.5) * 255;
    out.data[p + 3] = 255;
  }
  return out;
}

/**
 * 러프니스. 미세 요철이 많으면 거칠고, 넓게 매끄러운 하이라이트가 있으면 반들거린다.
 * 사람이 사진을 보고 "이거 금속이네/천이네" 하는 판단의 조잡한 대용품.
 */
export function buildRoughness(
  detail: Field,
  luma: Field,
  src: Raster,
  mask: Field,
  bias: number,
): Field {
  const radius = Math.max(2, Math.round(Math.max(detail.width, detail.height) / 220));
  const variance = normalizeField(localVariance(detail, radius), 0.02, 0.97, mask);

  const specular = makeField(src.width, src.height);
  for (let i = 0, p = 0; i < specular.data.length; i++, p += 4) {
    const sat = saturationAt(src.data, p);
    specular.data[i] = smoothstep(0.7, 0.97, luma.data[i]!) * (1 - smoothstep(0.12, 0.5, sat));
  }
  // 하이라이트 주변까지 매끄럽게 — 점 하나만 반들거리면 얼룩으로 보인다.
  const specSpread = boxBlur(specular, radius * 3);

  return mapField(variance, (v, i) => {
    const rough = 0.62 + v * 0.34 - specSpread.data[i]! * 0.55 + bias;
    return clamp(rough, 0.04, 1);
  });
}

/**
 * 메탈니스. 채도가 낮으면서 국소 명암 대비가 큰 곳 = 금속의 특징.
 * 확신이 안 서면 0에 붙인다 — 잘못 금속으로 찍으면 새까맣게 죽는다.
 */
export function buildMetalness(
  src: Raster,
  luma: Field,
  detail: Field,
  mask: Field,
  bias: number,
): Field {
  const radius = Math.max(2, Math.round(Math.max(src.width, src.height) / 160));
  const contrast = normalizeField(localVariance(luma, radius), 0.05, 0.98, mask);
  const out = makeField(src.width, src.height);

  for (let i = 0, p = 0; i < out.data.length; i++, p += 4) {
    const sat = saturationAt(src.data, p);
    const achromatic = 1 - smoothstep(0.08, 0.38, sat);
    const punchy = smoothstep(0.15, 0.65, contrast.data[i]!);
    const bright = smoothstep(0.08, 0.5, luma.data[i]!);
    out.data[i] = clamp(achromatic * punchy * bright * 0.9 + bias);
  }
  return gaussianBlur(out, Math.max(1, radius));
}

/** 캐비티 AO. 저주파를 뺀 뒤 남은 어두운 골짜기. */
export function buildAO(detail: Field, distance: Field, mask: Field, strength: number): Field {
  const cavity = normalizeField(mapField(detail, (v) => Math.max(0, -v)), 0.02, 0.98, mask);
  // 실루엣 근처는 다른 면에 가려질 확률이 높다 — 약하게 눌러준다.
  return mapField(cavity, (c, i) => {
    const edge = 1 - smoothstep(0, 0.22, distance.data[i]!);
    const occ = clamp(c * 0.85 + edge * 0.35);
    return clamp(1 - occ * strength);
  });
}

/** 발광부. 밝으면서 **채도까지 높은** 곳만. 흰 하이라이트는 스페큘러지 발광이 아니다. */
export function buildEmissive(src: Raster, luma: Field, mask: Field, strength: number): Raster {
  const out = makeRaster(src.width, src.height);
  if (strength <= 0.001) return out;

  for (let i = 0, p = 0; i < luma.data.length; i++, p += 4) {
    const m = mask.data[i]!;
    const sat = saturationAt(src.data, p);
    const glow = smoothstep(0.55, 0.95, luma.data[i]!) * smoothstep(0.3, 0.75, sat) * m * strength;
    out.data[p] = src.data[p]! * glow;
    out.data[p + 1] = src.data[p + 1]! * glow;
    out.data[p + 2] = src.data[p + 2]! * glow;
    out.data[p + 3] = 255;
  }
  return out;
}

/** AO/러프/메탈을 glTF ORM 한 장으로 묶는다. 텍스처 슬롯 세 개를 하나로 아낀다. */
function packORM(ao: Field, roughness: Field, metalness: Field): Raster {
  const out = makeRaster(ao.width, ao.height);
  for (let i = 0, p = 0; i < ao.data.length; i++, p += 4) {
    out.data[p] = clamp(ao.data[i]!) * 255;
    out.data[p + 1] = clamp(roughness.data[i]!) * 255;
    out.data[p + 2] = clamp(metalness.data[i]!) * 255;
    out.data[p + 3] = 255;
  }
  return out;
}

export function buildMaterialMaps(analysis: ImageAnalysis, params: ForgeParams): MaterialMaps {
  const { mask, distance } = analysis;
  // 실루엣이 있을 때만 번진다. 화면 전체가 피사체면 테두리가 없다.
  const source = analysis.maskSource === 'full' ? analysis.source : bleedEdges(analysis.source, mask, 2, 12);
  const luma = source === analysis.source ? analysis.luma : luminanceField(source);
  const maxDim = Math.max(source.width, source.height);

  const detail = surfaceDetail(luma, maxDim);
  const albedo = delight(source, luma, mask, params.delight);
  const normal = buildNormal(detail, mask, params.normalStrength);
  const roughness = buildRoughness(detail, luma, source, mask, params.roughnessBias);
  const metalness = buildMetalness(source, luma, detail, mask, params.metalnessBias);
  const ao = buildAO(detail, distance, mask, params.aoStrength);
  const emissive = buildEmissive(source, luma, mask, params.emissiveStrength);

  return {
    albedo,
    normal,
    orm: packORM(ao, roughness, metalness),
    emissive,
    alpha: cloneField(mask),
  };
}
